import React, { createContext, useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { NativeLayerContext, NativeLayerProviderProps, NativeMessage } from '../types/nativeLayer'

const defaultContext: NativeLayerContext = {
	isAvailable: false,
	isHandshakeComplete: false,
	isReady: false,
	close: () => {},
	transactionSuccess: () => {},
	transactionFailure: () => {},
}

export const NativeLayerContextProvider = createContext<NativeLayerContext>(defaultContext)

export const NativeLayerProvider: React.FC<NativeLayerProviderProps> = ({ children }) => {
	const [isAvailable, setIsAvailable] = useState<boolean>(false)
	const [isHandshakeComplete, setIsHandshakeComplete] = useState<boolean>(false)
	const [isHandshakeInProgress, setIsHandshakeInProgress] = useState<boolean>(false)
	const [handshakeMessageId, setHandshakeMessageId] = useState<string | null>(null)

	// Check if native layer is available and automatically initiate handshake
	useEffect(() => {
		// Handle incoming messages from native layer via TezPay.postMessage
		const processMessage = (data: string | NativeMessage) => {
			if (!isAvailable) return

			try {
				const message = typeof data === 'string' ? JSON.parse(data) : data
				// Check if this is a handshake response by matching replyId to our handshake message ID
				if (message.type === 'handshake' && message.replyId === handshakeMessageId) {
					setIsHandshakeComplete(true)
					setIsHandshakeInProgress(false)
					setHandshakeMessageId(null) // Clear the handshake message ID
				}
			} catch (error) {
				// Log error only if NativeLayer is available (we expect valid messages)
				console.error('NativeLayer received invalid message:', error, data)
			}
		}

		const checkNativeLayer = () => {
			const available = typeof window.NativeLayer?.postMessage === 'function'
			setIsAvailable(available)

			if (available) {
				// First, set up TezPay.postMessage to receive responses
				window.TezPay = {
					postMessage: processMessage,
				}

				// Then send handshake after TezPay is ready
				if (!isHandshakeComplete && !isHandshakeInProgress) {
					setIsHandshakeInProgress(true)
					const newHandshakeMessageId = uuidv4()
					setHandshakeMessageId(newHandshakeMessageId)
					const handshakeMessage: NativeMessage = {
						id: newHandshakeMessageId,
						type: 'handshake',
					}
					try {
						window.NativeLayer!.postMessage(JSON.stringify(handshakeMessage))
					} catch (error) {
						console.error('NativeLayer handshake failed:', error)
						setIsHandshakeInProgress(false) // Reset on error
						setHandshakeMessageId(null) // Clear on error
					}
				}
			}
		}

		checkNativeLayer()

		return () => {
			if (isAvailable) {
				// Clean up TezPay if we set it up
				if (window.TezPay?.postMessage === processMessage) {
					delete window.TezPay
				}
			}
		}
	}, [isAvailable, isHandshakeComplete, isHandshakeInProgress, handshakeMessageId])

	const postMessage = useCallback(
		(message: NativeMessage) => {
			if (isAvailable && window.NativeLayer) {
				try {
					window.NativeLayer.postMessage(JSON.stringify(message))
				} catch (error) {
					console.error('NativeLayer message failed:', message.type, error)
				}
			}
			// If NativeLayer is not available, silently do nothing
		},
		[isAvailable]
	)

	const close = useCallback(() => {
		if (!isAvailable) {
			return // Silently do nothing if NativeLayer is not available
		}
		const message: NativeMessage = {
			id: uuidv4(),
			type: 'close',
		}
		postMessage(message)
	}, [postMessage, isAvailable])

	const transactionSuccess = useCallback(() => {
		const isReady = isAvailable && isHandshakeComplete
		if (!isReady) {
			return // Silently do nothing if NativeLayer is not ready
		}
		const message: NativeMessage = {
			id: uuidv4(),
			type: 'transaction_success',
		}
		postMessage(message)
	}, [postMessage, isAvailable, isHandshakeComplete])

	const transactionFailure = useCallback(() => {
		const isReady = isAvailable && isHandshakeComplete
		if (!isReady) {
			return // Silently do nothing if NativeLayer is not ready
		}
		const message: NativeMessage = {
			id: uuidv4(),
			type: 'transaction_failure',
		}
		postMessage(message)
	}, [postMessage, isAvailable, isHandshakeComplete])

	// Computed isReady property
	const isReady = isAvailable && isHandshakeComplete

	const contextValue: NativeLayerContext = {
		isAvailable,
		isHandshakeComplete,
		isReady,
		close,
		transactionSuccess,
		transactionFailure,
	}

	return <NativeLayerContextProvider.Provider value={contextValue}>{children}</NativeLayerContextProvider.Provider>
}
